import { useEffect, useState, useCallback } from "react";
import { api, API, getToken } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Plus, User, Building2, FileText, Trash2, FilePlus, History, MessageCircle, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { WhatsAppLink } from "@/components/WhatsAppLink";
import { SmsButton } from "@/components/SmsButton";
import { useAuth } from "@/context/AuthContext";
import { isInternal, fmtDateTime } from "@/lib/constants";
import { ExportDialog, ANAGRAFICHE_EXPORT_FIELDS, DATE_FIELDS_BASE } from "@/components/ExportDialog";
import { useExports } from "@/context/ExportsContext";

const EMPTY = { tipo: "fisica", nome: "", cognome: "", codice_fiscale: "", data_nascita: "", ragione_sociale: "", partita_iva: "", referente: "", email: "", telefono: "", indirizzo: "", citta: "", provincia: "", cellulare: "", telefono_fisso: "", comune_nascita: "", provincia_nascita: "", occupazione: "", professione: "", reddito_mensile: "", azienda_lavoro: "" };

export default function Anagrafiche() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const internal = isInternal(user?.role);
  const [anagHist, setAnagHist] = useState(null);
  const [items, setItems] = useState([]);
  const FILTER_EMPTY = { nome: "", cognome: "", cf: "", citta: "", provincia: "", professione: "", tipo: "all", created_from: "", created_to: "", updated_from: "", updated_to: "" };
  const [f, setF] = useState(FILTER_EMPTY);
  const [exportOpen, setExportOpen] = useState(false);
  const { startExport } = useExports();
  const onExport = (params) => {
    const active = {};
    Object.entries(f).forEach(([k, v]) => { if (v && v !== "all") active[k] = v; });
    startExport("anagrafiche_export", { ...active, ...params }, "Anagrafiche");
  };
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [detail, setDetail] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const [campaigns, setCampaigns] = useState([]);
  const [sel, setSel] = useState([]);
  const [targetCamp, setTargetCamp] = useState("");

  const load = useCallback(() => {
    const params = { page, page_size: pageSize };
    Object.entries(f).forEach(([k, v]) => { if (v && v !== "all") params[k] = v; });
    api.get("/anagrafiche", { params }).then((r) => { setItems(r.data.items || []); setTotal(r.data.total || 0); setSel([]); });
  }, [f, page, pageSize]);
  const setFilter = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const hasFilters = Object.entries(f).some(([, v]) => v && v !== "all");

  const toggleSel = (id) => setSel((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  const allSelected = items.length > 0 && items.every((a) => sel.includes(a.id));
  const toggleAll = () => setSel(allSelected ? [] : items.map((a) => a.id));
  const assignToCampaign = async (ids) => {
    const list = ids || sel;
    if (!targetCamp) { toast.error("Scegli una campagna"); return; }
    if (list.length === 0) { toast.error("Seleziona almeno un'anagrafica"); return; }
    try {
      const { data } = await api.post(`/campaigns/${targetCamp}/leads/from-anagrafiche`, { anagrafiche_ids: list });
      toast.success(`${data.created} anagrafiche associate · ${data.skipped_duplicate} duplicate`);
      setSel([]); setTargetCamp("");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore"); }
  };

  useEffect(() => { setPage(1); }, [f]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  useEffect(() => {
    const openId = new URLSearchParams(window.location.search).get("open");
    if (openId && items.length) {
      const a = items.find((x) => x.id === openId);
      if (a) { setDetail(a); window.history.replaceState({}, "", "/anagrafiche"); }
    }
  }, [items]);

  useEffect(() => { if (internal) api.get("/campaigns").then((r) => setCampaigns(r.data)).catch(() => {}); }, [internal]);

  useEffect(() => {
    if (detail && internal) {
      setAnagHist(null);
      api.get(`/anagrafiche/${detail.id}/history`).then((r) => setAnagHist(r.data)).catch(() => setAnagHist(null));
    }
  }, [detail, internal]);

  const create = async () => {
    if (form.tipo === "fisica" && !form.nome && !form.cognome) { toast.error("Inserisci nome e cognome"); return; }
    if (form.tipo === "giuridica" && !form.ragione_sociale) { toast.error("Inserisci la ragione sociale"); return; }
    try {
      await api.post("/anagrafiche", form);
      toast.success("Anagrafica creata");
      setOpen(false); setForm(EMPTY); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore nella creazione"); }
  };

  const remove = async (id) => { await api.delete(`/anagrafiche/${id}`); load(); toast.success("Anagrafica eliminata"); };

  const anagName = (a) => a.tipo === "giuridica" ? a.ragione_sociale : `${a.nome || ""} ${a.cognome || ""}`.trim();

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Anagrafiche</h1>
          <p className="text-sm text-muted-foreground mt-1">{total} soggetti registrati</p>
        </div>
        <div className="flex gap-2">
          {internal && (
            <Button variant="outline" data-testid="anagrafiche-export-btn" onClick={() => setExportOpen(true)}>
              <FileText className="h-4 w-4 mr-2" /> Excel
            </Button>
          )}
          <ExportDialog open={exportOpen} onOpenChange={setExportOpen} title="Esporta Anagrafiche (filtri attivi)" fields={ANAGRAFICHE_EXPORT_FIELDS} dateFields={DATE_FIELDS_BASE} onConfirm={onExport} />
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-anagrafica-btn"><Plus className="h-4 w-4 mr-2" /> Nuova Anagrafica</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Nuova Anagrafica</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-1.5">
                <Label>Tipo soggetto</Label>
                <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                  <SelectTrigger data-testid="anagrafica-type-toggle"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fisica">Persona Fisica</SelectItem>
                    <SelectItem value="giuridica">Persona Giuridica (Azienda)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.tipo === "fisica" ? (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5"><Label>Nome</Label><Input data-testid="anag-nome" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} /></div>
                    <div className="space-y-1.5"><Label>Cognome</Label><Input data-testid="anag-cognome" value={form.cognome} onChange={(e) => setForm({ ...form, cognome: e.target.value })} /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5"><Label>Codice Fiscale</Label><Input data-testid="anag-cf" value={form.codice_fiscale} onChange={(e) => setForm({ ...form, codice_fiscale: e.target.value })} /></div>
                    <div className="space-y-1.5"><Label>Data di nascita</Label><Input type="date" value={form.data_nascita} onChange={(e) => setForm({ ...form, data_nascita: e.target.value })} /></div>
                  </div>
                </>
              ) : (
                <>
                  <div className="space-y-1.5"><Label>Ragione Sociale</Label><Input data-testid="anag-ragione" value={form.ragione_sociale} onChange={(e) => setForm({ ...form, ragione_sociale: e.target.value })} /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5"><Label>Partita IVA</Label><Input data-testid="anag-piva" value={form.partita_iva} onChange={(e) => setForm({ ...form, partita_iva: e.target.value })} /></div>
                    <div className="space-y-1.5"><Label>Referente</Label><Input value={form.referente} onChange={(e) => setForm({ ...form, referente: e.target.value })} /></div>
                  </div>
                </>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Email</Label><Input data-testid="anag-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Cellulare</Label><Input data-testid="anag-cellulare" value={form.cellulare} onChange={(e) => setForm({ ...form, cellulare: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Telefono Fisso</Label><Input data-testid="anag-telefono-fisso" value={form.telefono_fisso} onChange={(e) => setForm({ ...form, telefono_fisso: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Telefono (alt.)</Label><Input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} /></div>
              </div>
              <div className="space-y-1.5"><Label>Indirizzo</Label><Input value={form.indirizzo} onChange={(e) => setForm({ ...form, indirizzo: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Città</Label><Input data-testid="anag-citta" value={form.citta} onChange={(e) => setForm({ ...form, citta: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Provincia</Label><Input data-testid="anag-provincia" maxLength={2} value={form.provincia} onChange={(e) => setForm({ ...form, provincia: e.target.value.toUpperCase() })} placeholder="Es. LT" /></div>
              </div>
              {form.tipo === "fisica" && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5"><Label>Comune di nascita</Label><Input data-testid="anag-comune-nascita" value={form.comune_nascita} onChange={(e) => setForm({ ...form, comune_nascita: e.target.value })} /></div>
                    <div className="space-y-1.5"><Label>Provincia di nascita</Label><Input maxLength={2} value={form.provincia_nascita} onChange={(e) => setForm({ ...form, provincia_nascita: e.target.value.toUpperCase() })} /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5"><Label>Occupazione</Label><Input data-testid="anag-occupazione" value={form.occupazione} onChange={(e) => setForm({ ...form, occupazione: e.target.value })} placeholder="Es. Dipendente t. indet." /></div>
                    <div className="space-y-1.5"><Label>Professione</Label><Input value={form.professione} onChange={(e) => setForm({ ...form, professione: e.target.value })} /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5"><Label>Reddito mensile</Label><Input data-testid="anag-reddito" value={form.reddito_mensile} onChange={(e) => setForm({ ...form, reddito_mensile: e.target.value })} placeholder="€" /></div>
                    <div className="space-y-1.5"><Label>Azienda di lavoro</Label><Input value={form.azienda_lavoro} onChange={(e) => setForm({ ...form, azienda_lavoro: e.target.value })} /></div>
                  </div>
                </>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
              <Button data-testid="anagrafica-submit" onClick={create}>Salva</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <Card className="p-4">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          <div><label className="text-xs text-muted-foreground">Nome</label><Input data-testid="anag-filter-nome" value={f.nome} onChange={(e) => setFilter("nome", e.target.value)} placeholder="Nome" /></div>
          <div><label className="text-xs text-muted-foreground">Cognome / Ragione soc.</label><Input data-testid="anag-filter-cognome" value={f.cognome} onChange={(e) => setFilter("cognome", e.target.value)} placeholder="Cognome / Ragione soc." /></div>
          <div><label className="text-xs text-muted-foreground">CF / P.IVA</label><Input data-testid="anag-filter-cf" value={f.cf} onChange={(e) => setFilter("cf", e.target.value)} placeholder="CF / P.IVA" /></div>
          <div><label className="text-xs text-muted-foreground">Tipo</label>
            <Select value={f.tipo} onValueChange={(v) => setFilter("tipo", v)}>
              <SelectTrigger data-testid="anag-filter-tipo"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i tipi</SelectItem>
                <SelectItem value="fisica">Persona Fisica</SelectItem>
                <SelectItem value="giuridica">Persona Giuridica</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><label className="text-xs text-muted-foreground">Città</label><Input data-testid="anag-filter-citta" value={f.citta} onChange={(e) => setFilter("citta", e.target.value)} placeholder="Città" /></div>
          <div><label className="text-xs text-muted-foreground">Provincia</label><Input data-testid="anag-filter-provincia" value={f.provincia} onChange={(e) => setFilter("provincia", e.target.value)} placeholder="Provincia" /></div>
          <div><label className="text-xs text-muted-foreground">Professione</label><Input data-testid="anag-filter-professione" value={f.professione} onChange={(e) => setFilter("professione", e.target.value)} placeholder="Professione" /></div>
          <div><label className="text-xs text-muted-foreground">Caricato dal</label><Input type="date" data-testid="anag-filter-created-from" value={f.created_from} onChange={(e) => setFilter("created_from", e.target.value)} /></div>
          <div><label className="text-xs text-muted-foreground">Caricato al</label><Input type="date" data-testid="anag-filter-created-to" value={f.created_to} onChange={(e) => setFilter("created_to", e.target.value)} /></div>
          <div><label className="text-xs text-muted-foreground">Modificato dal</label><Input type="date" data-testid="anag-filter-updated-from" value={f.updated_from} onChange={(e) => setFilter("updated_from", e.target.value)} /></div>
          <div><label className="text-xs text-muted-foreground">Modificato al</label><Input type="date" data-testid="anag-filter-updated-to" value={f.updated_to} onChange={(e) => setFilter("updated_to", e.target.value)} /></div>
        </div>
        {hasFilters && <Button variant="ghost" size="sm" className="mt-3" data-testid="anag-reset-filters" onClick={() => setF(FILTER_EMPTY)}><X className="h-3.5 w-3.5 mr-1" />Azzera filtri</Button>}
      </Card>

      {internal && sel.length > 0 && (
        <Card className="p-3 flex flex-wrap items-center gap-3" data-testid="anag-bulk-bar">
          <span className="text-sm font-medium">{sel.length} anagrafiche selezionate</span>
          <div className="flex flex-wrap items-center gap-2 ml-auto">
            <Select value={targetCamp} onValueChange={setTargetCamp}>
              <SelectTrigger className="w-56" data-testid="anag-assign-campaign"><SelectValue placeholder="Associa a campagna…" /></SelectTrigger>
              <SelectContent>{campaigns.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}</SelectContent>
            </Select>
            <Button data-testid="anag-assign-btn" onClick={() => assignToCampaign()}>Associa selezionate</Button>
            <Button variant="outline" data-testid="anag-assign-all-btn" onClick={() => assignToCampaign(items.map((a) => a.id))}>Associa tutti i risultati ({items.length})</Button>
            <Button variant="ghost" data-testid="anag-assign-cancel" onClick={() => setSel([])}>Annulla</Button>
          </div>
        </Card>
      )}

      <Card className="overflow-hidden">
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm" data-testid="anagrafiche-table">
            <thead className="bg-muted/50 border-b border-border">
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                {internal && <th className="px-3 py-3 w-8"><input type="checkbox" className="accent-primary" checked={allSelected} onChange={toggleAll} data-testid="anag-select-all" /></th>}
                <th className="px-4 py-3 font-semibold">Nominativo / Ragione Sociale</th>
                <th className="px-4 py-3 font-semibold">Tipo</th>
                <th className="px-4 py-3 font-semibold">CF / P.IVA</th>
                <th className="px-4 py-3 font-semibold">Email</th>
                <th className="px-4 py-3 font-semibold">Telefono</th>
                <th className="px-4 py-3 font-semibold">Città</th>
                <th className="px-4 py-3 font-semibold text-center">Pratiche</th>
                <th className="px-4 py-3 font-semibold text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.id} data-testid={`anagrafica-row-${a.id}`} onClick={() => setDetail(a)}
                    className="border-b border-border last:border-0 hover:bg-accent/40 cursor-pointer">
                  {internal && <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}><input type="checkbox" className="accent-primary" checked={sel.includes(a.id)} onChange={() => toggleSel(a.id)} data-testid={`anag-check-${a.id}`} /></td>}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <div className={`flex h-8 w-8 items-center justify-center rounded-md shrink-0 ${a.tipo === "giuridica" ? "bg-indigo-500/10 text-indigo-500" : "bg-emerald-500/10 text-emerald-500"}`}>
                        {a.tipo === "giuridica" ? <Building2 className="h-4 w-4" /> : <User className="h-4 w-4" />}
                      </div>
                      <span className="font-medium">{anagName(a) || "—"}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{a.tipo === "giuridica" ? "Giuridica" : "Fisica"}</td>
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{a.tipo === "giuridica" ? (a.partita_iva || "—") : (a.codice_fiscale || "—")}</td>
                  <td className="px-4 py-3 text-muted-foreground truncate max-w-[200px]">{a.email || "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground"><span className="inline-flex items-center gap-1.5">{a.cellulare || a.telefono || a.telefono_fisso || "—"}{a.cellulare && <WhatsAppLink number={a.cellulare} />}</span></td>
                  <td className="px-4 py-3 text-muted-foreground">{a.citta ? `${a.citta}${a.provincia ? ` (${a.provincia})` : ""}` : "—"}</td>
                  <td className="px-4 py-3 text-center">
                    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><FileText className="h-3.5 w-3.5" />{a.practice_count}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                      <Button variant="outline" size="sm" data-testid={`crea-pratica-${a.id}`} onClick={() => navigate(`/pratiche?anagrafica=${a.id}`)}><FilePlus className="h-3.5 w-3.5 mr-1.5" />Crea pratica</Button>
                      <Button variant="ghost" size="icon" data-testid={`anagrafica-delete-${a.id}`} onClick={() => remove(a.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={internal ? 9 : 8} className="px-4 py-12 text-center text-muted-foreground"><User className="h-8 w-8 mx-auto mb-2 opacity-40" />Nessuna anagrafica trovata</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="md:hidden divide-y divide-border" data-testid="anagrafiche-mobile-list">
          {items.map((a) => (
            <div key={a.id} data-testid={`anagrafica-card-${a.id}`} onClick={() => setDetail(a)} className="p-4 hover:bg-accent/40 cursor-pointer">
              <div className="flex items-center gap-2.5">
                {internal && <input type="checkbox" className="accent-primary" checked={sel.includes(a.id)} onClick={(e) => e.stopPropagation()} onChange={() => toggleSel(a.id)} data-testid={`anag-check-m-${a.id}`} />}
                <div className={`flex h-8 w-8 items-center justify-center rounded-md shrink-0 ${a.tipo === "giuridica" ? "bg-indigo-500/10 text-indigo-500" : "bg-emerald-500/10 text-emerald-500"}`}>
                  {a.tipo === "giuridica" ? <Building2 className="h-4 w-4" /> : <User className="h-4 w-4" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium break-words">{anagName(a) || "—"}</div>
                  <div className="text-xs text-muted-foreground font-mono">{a.tipo === "giuridica" ? (a.partita_iva || "—") : (a.codice_fiscale || "—")}</div>
                </div>
                <span className="inline-flex items-center gap-1 text-xs text-muted-foreground shrink-0"><FileText className="h-3.5 w-3.5" />{a.practice_count}</span>
              </div>
              <div className="mt-2 grid grid-cols-1 gap-y-1 text-xs text-muted-foreground">
                {a.email && <div className="break-words"><span className="font-medium text-foreground">Email:</span> {a.email}</div>}
                <div className="inline-flex items-center gap-1.5"><span className="font-medium text-foreground">Tel:</span> {a.cellulare || a.telefono || a.telefono_fisso || "—"}{a.cellulare && <WhatsAppLink number={a.cellulare} />}</div>
                {a.citta && <div><span className="font-medium text-foreground">Città:</span> {`${a.citta}${a.provincia ? ` (${a.provincia})` : ""}`}</div>}
              </div>
              <div className="mt-2 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                <Button variant="outline" size="sm" data-testid={`crea-pratica-m-${a.id}`} onClick={() => navigate(`/pratiche?anagrafica=${a.id}`)}><FilePlus className="h-3.5 w-3.5 mr-1.5" />Crea pratica</Button>
                <Button variant="ghost" size="icon" data-testid={`anagrafica-delete-m-${a.id}`} onClick={() => remove(a.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
              </div>
            </div>
          ))}
          {items.length === 0 && <div className="px-4 py-12 text-center text-muted-foreground"><User className="h-8 w-8 mx-auto mb-2 opacity-40" />Nessuna anagrafica trovata</div>}
        </div>
        {total > pageSize && (
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-border">
            <span className="text-sm text-muted-foreground" data-testid="anag-page-info">Pagina {page} di {pageCount} · {total} anagrafiche</span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" data-testid="anag-prev-page" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Precedente</Button>
              <Button variant="outline" size="sm" data-testid="anag-next-page" disabled={page >= pageCount} onClick={() => setPage((p) => Math.min(pageCount, p + 1))}>Successiva</Button>
            </div>
          </div>
        )}
      </Card>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="anagrafica-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2.5">
                  <div className={`flex h-9 w-9 items-center justify-center rounded-lg shrink-0 ${detail.tipo === "giuridica" ? "bg-indigo-500/10 text-indigo-500" : "bg-emerald-500/10 text-emerald-500"}`}>
                    {detail.tipo === "giuridica" ? <Building2 className="h-5 w-5" /> : <User className="h-5 w-5" />}
                  </div>
                  {anagName(detail) || "Anagrafica"}
                </DialogTitle>
                <DialogDescription>Scheda anagrafica con dati di contatto, pratiche collegate e storico.</DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-x-6 gap-y-3 py-2 text-sm">
                {detail.tipo === "giuridica" ? [
                  ["Ragione Sociale", detail.ragione_sociale], ["Partita IVA", detail.partita_iva], ["Referente", detail.referente],
                ] : [
                  ["Nome", detail.nome], ["Cognome", detail.cognome], ["Codice Fiscale", detail.codice_fiscale],
                  ["Data di nascita", detail.data_nascita], ["Comune di nascita", detail.comune_nascita], ["Provincia di nascita", detail.provincia_nascita],
                  ["Occupazione", detail.occupazione], ["Professione", detail.professione], ["Reddito mensile", detail.reddito_mensile], ["Azienda di lavoro", detail.azienda_lavoro],
                ].concat([
                  ["Email", detail.email], ["Cellulare", detail.cellulare], ["Telefono fisso", detail.telefono_fisso], ["Telefono (alt.)", detail.telefono],
                  ["Indirizzo", detail.indirizzo], ["Città", detail.citta], ["Provincia", detail.provincia],
                ]).map(([label, value]) => (
                  <div key={label} className="space-y-0.5">
                    <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
                    <p className="font-medium break-words inline-flex items-center gap-1.5">{value || "—"}{label === "Cellulare" && value && <WhatsAppLink number={value} message={`Buongiorno ${detail.nome || ""},`.trim()} />}</p>
                  </div>
                ))}
              </div>
              {(detail.cellulare || detail.telefono || detail.telefono_fisso) && (
                <div className="flex flex-wrap gap-2 border-t border-border pt-3">
                  <SmsButton size="sm" recipients={[{ name: anagName(detail), number: detail.cellulare || detail.telefono || detail.telefono_fisso }]} defaultMessage={`Buongiorno ${detail.nome || ""},`.trim()} context={{ type: "anagrafica", id: detail.id }} />
                </div>
              )}
              {internal && anagHist && (anagHist.history.length > 0 || anagHist.campaigns.length > 0) && (
                <div className="mt-2 border-t border-border pt-3" data-testid="anag-history">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <h3 className="font-heading font-semibold text-sm flex items-center gap-2"><History className="h-4 w-4 text-primary" /> Storico note (campagne)</h3>
                    {anagHist.campaigns.length > 0 && <span className="text-[11px] rounded-full bg-primary/10 text-primary px-2 py-0.5 font-semibold">In {anagHist.campaigns.length} campagne</span>}
                  </div>
                  {anagHist.campaigns.length > 0 && (
                    <p className="text-xs text-muted-foreground mb-2">Presente in: {anagHist.campaigns.map((c) => c.nome).join(", ")}</p>
                  )}
                  <div className="space-y-2 max-h-56 overflow-y-auto">
                    {[...anagHist.history].reverse().map((h, i) => (
                      <div key={i} className="rounded-lg border border-border p-2.5">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-medium">{h.esito}</span>
                          <span className="text-[11px] text-muted-foreground">{h.by} · {fmtDateTime(h.at)}</span>
                        </div>
                        {h.nota && <p className="text-xs text-muted-foreground mt-0.5">{h.nota}</p>}
                        <p className="text-[10px] uppercase tracking-wider mt-1 flex items-center gap-1 text-muted-foreground"><MessageCircle className="h-3 w-3" />Campagna: <span className="font-medium">{h.campaign_name}</span></p>
                      </div>
                    ))}
                    {anagHist.history.length === 0 && <p className="text-sm text-muted-foreground">Nessuna nota registrata nelle campagne.</p>}
                  </div>
                </div>
              )}
              <DialogFooter className="flex-col sm:flex-row gap-2">
                <span className="text-xs text-muted-foreground mr-auto flex items-center gap-1.5"><FileText className="h-3.5 w-3.5" />{detail.practice_count} pratiche collegate</span>
                <Button variant="outline" onClick={() => setDetail(null)}>Chiudi</Button>
                <Button data-testid="detail-crea-pratica" onClick={() => navigate(`/pratiche?anagrafica=${detail.id}`)}><FilePlus className="h-4 w-4 mr-1.5" />Crea pratica</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
